/**
 * Design System Tokens
 * 
 * Centralized design tokens matching landing page design.
 * Uses landing page color scheme: warm terracotta/coral for light, deep blues for dark.
 * 
 * Light Theme: Warm, women-friendly tones (#faf8f5 backgrounds, #d9776f accents)
 * Dark Theme: Deep dark blues (#0a0f1c backgrounds, #60a5fa accents)
 */

export const designTokens = {
  light: {
    // Backgrounds - Warm, women-friendly tones
    bg: {
      primary: 'bg-[#faf8f5]',           // --bg: #faf8f5
      secondary: 'bg-[#fffefb]',          // --panel: #fffefb
      tertiary: 'bg-[#fffefb]',         // --card: #fffefb
      panel: 'bg-[#f5f1eb]',            // Panel/sidebar - slightly darker than main bg
      gradient: 'bg-gradient-to-br from-[#faf8f5] via-[#f5f1eb] to-[#faf8f5]', // Matching landing gradient
    },
    // Text Colors - Warm browns
    text: {
      primary: 'text-[#3d3529]',         // --text: #3d3529
      secondary: 'text-[#3d3529]',       // High contrast
      tertiary: 'text-[#3d3529]',       // Body text
      muted: 'text-[#8b7d6b]',           // --muted: #8b7d6b
      inverse: 'text-[#fffefb]',
      accent: 'text-[#d9776f]',          // Accent color for links/highlights
    },
    // Borders - Warm tones
    border: {
      light: 'border-[rgba(141,125,107,0.15)]',  // --border: rgba(141,125,107,0.15)
      medium: 'border-[rgba(141,125,107,0.25)]',
      strong: 'border-[rgba(141,125,107,0.35)]',
      accent: 'border-[#d9776f]',                // --accent: #d9776f
      accentStrong: 'border-[#c0847c]',           // --accent-2: #c0847c
    },
    // Cards - Warm whites
    card: {
      bg: 'bg-[#fffefb]',                // --card: #fffefb
      border: 'border-[rgba(141,125,107,0.15)]',
      shadow: 'shadow-[0_18px_40px_rgba(141,125,107,0.12)]', // --shadow
    },
    // Buttons - Terracotta/coral accents
    button: {
      primary: 'bg-gradient-to-r from-[#d9776f] to-[#c0847c] hover:from-[#c85a4f] hover:to-[#a86b5f] text-[#fffefb] rounded-landing font-semibold transition-all active:scale-95 min-h-[48px] shadow-sm', // --accent gradient
      secondary: 'bg-[rgba(217,119,111,0.15)] hover:bg-[rgba(217,119,111,0.25)] text-[#3d3529] rounded-landing font-semibold transition-all active:scale-95 min-h-[48px]',
      outline: 'bg-[#fffefb] border-2 border-[rgba(141,125,107,0.25)] hover:bg-[rgba(141,125,107,0.05)] text-[#3d3529] rounded-landing font-semibold transition-all active:scale-95 min-h-[48px]',
    },
    // Accents - Terracotta/coral palette
    accent: {
      primary: 'bg-[#d9776f]',          // --accent: #d9776f
      secondary: 'bg-[#c0847c]',        // --accent-2: #c0847c
      tertiary: 'bg-[#a78b7a]',        // --accent-3: #a78b7a
    },
    // Toast colors - Peachy palette (T4 design) - Solid backgrounds
    toast: {
      success: {
        bg: 'bg-[#ffdab9]',                      // Solid peach
        border: 'border-[#ffb799]',
        text: 'text-[#d97757]',                 // Warm peachy coral
      },
      error: {
        bg: 'bg-[#ffb6c1]',                      // Solid peachy rose
        border: 'border-[#ff9a9e]',
        text: 'text-[#e57373]',                 // Deeper peachy rose
      },
      info: {
        bg: 'bg-[#ffdab9]',                      // Solid soft peachy
        border: 'border-[#ffc8b4]',
        text: 'text-[#d4a574]',                // Warm peachy beige
      },
      loading: {
        bg: 'bg-[#ffe7cd]',                      // Solid light peachy
        border: 'border-[#ffdab9]',
        text: 'text-[#c9a882]',                // Neutral peachy
      },
    },
  },
  dark: {
    // Backgrounds - Deep dark blues
    bg: {
      primary: 'bg-[#0a0f1c]',          // --bg: #0a0f1c
      secondary: 'bg-[#0f172a]',        // --panel: #0f172a
      tertiary: 'bg-[#111827]',         // --card: #111827
      panel: 'bg-[#0f172a]',            // Panel/sidebar - darker than main bg
      gradient: 'bg-gradient-to-br from-[#0b1222] via-[#0a0f1c] to-[#0a0f1c]', // Matching landing gradient
    },
    // Text Colors - Light grays
    text: {
      primary: 'text-[#e5e7eb]',        // --text: #e5e7eb
      secondary: 'text-[#e5e7eb]',      // High contrast
      tertiary: 'text-[#e5e7eb]',       // Body text
      muted: 'text-[#94a3b8]',          // --muted: #94a3b8
      inverse: 'text-[#0a0f1c]',
      accent: 'text-[#60a5fa]',         // Accent color for links/highlights
    },
    // Borders - Subtle whites
    border: {
      light: 'border-[rgba(255,255,255,0.04)]',  // --border: rgba(255,255,255,0.04)
      medium: 'border-[rgba(255,255,255,0.08)]',
      strong: 'border-[rgba(255,255,255,0.12)]',
      accent: 'border-[#60a5fa]',                 // --accent: #60a5fa
      accentStrong: 'border-[#a855f7]',           // --accent-2: #a855f7
    },
    // Cards - Dark blues
    card: {
      bg: 'bg-[#111827]',               // --card: #111827
      border: 'border-[rgba(255,255,255,0.04)]',
      shadow: 'shadow-[0_18px_40px_rgba(0,0,0,0.28)]', // --shadow
    },
    // Buttons - Blue accents
    button: {
      primary: 'bg-gradient-to-r from-[#60a5fa] to-[#a855f7] hover:from-[#3b82f6] hover:to-[#9333ea] text-[#0a0f1c] rounded-landing font-semibold transition-all active:scale-95 min-h-[48px] shadow-sm', // --accent gradient
      secondary: 'bg-[rgba(96,165,250,0.12)] hover:bg-[rgba(96,165,250,0.20)] text-[#e5e7eb] rounded-landing font-semibold transition-all active:scale-95 min-h-[48px]',
      outline: 'bg-[#111827] border-2 border-[rgba(255,255,255,0.08)] hover:bg-[rgba(255,255,255,0.05)] text-[#e5e7eb] rounded-landing font-semibold transition-all active:scale-95 min-h-[48px]',
    },
    // Accents - Blue/purple/green palette
    accent: {
      primary: 'bg-[#60a5fa]',          // --accent: #60a5fa
      secondary: 'bg-[#a855f7]',        // --accent-2: #a855f7
      tertiary: 'bg-[#22c55e]',        // --accent-3: #22c55e
    },
    // Toast colors - Peachy palette (T4 design) - Solid backgrounds
    toast: {
      success: {
        bg: 'bg-[#ffb799]',                      // Solid peach
        border: 'border-[#ff9a7a]',
        text: 'text-[#ffb799]',                 // Warm peachy coral
      },
      error: {
        bg: 'bg-[#ff9a9e]',                      // Solid peachy rose
        border: 'border-[#ff7a7e]',
        text: 'text-[#ff9a9e]',                 // Deeper peachy rose
      },
      info: {
        bg: 'bg-[#ffc8b4]',                      // Solid soft peachy
        border: 'border-[#ffb8a4]',
        text: 'text-[#ffc8b4]',                 // Warm peachy beige
      },
      loading: {
        bg: 'bg-[#ffdab9]',                      // Solid light peachy
        border: 'border-[#ffcab9]',
        text: 'text-[#ffdab9]',                 // Neutral peachy
      },
    },
  },
};

/**
 * Get design tokens for a specific mode
 */
export function getDesignTokens(isDarkMode: boolean) {
  return isDarkMode ? designTokens.dark : designTokens.light;
}
